'use client';

/**
 * CellText Helper Component
 *
 * A text wrapper with theme-aware inline styles for use in custom renderCell functions.
 *
 * ## Purpose
 *
 * When you need custom cell layouts (icons + text, complex structures), use this
 * component to ensure text receives proper theme styling without manual theme imports.
 *
 * ## Usage Examples
 *
 * ### Example 1: Icon + Text (like Provider column)
 * ```tsx
 * import { DataTable, CellText } from '@/components/shared/DataTable';
 *
 * const columns = [
 *   {
 *     id: 'provider',
 *     label: 'Provider',
 *     type: 'custom',
 *     renderCell: (value) => {
 *       const isGmail = value === 'google_email';
 *       return (
 *         <div className="flex items-center gap-2">
 *           <Mail className="h-4 w-4" />
 *           <CellText>{isGmail ? 'Gmail' : 'Outlook'}</CellText>
 *         </div>
 *       );
 *     }
 *   }
 * ];
 * ```
 *
 * ### Example 2: Primary + Secondary Text
 * ```tsx
 * renderCell: (value, row) => (
 *   <div>
 *     <CellText variant="primary">{row.name}</CellText>
 *     <CellText variant="secondary">{row.email}</CellText>
 *   </div>
 * )
 * ```
 *
 * ### Example 3: Badge + Text
 * ```tsx
 * renderCell: (value) => (
 *   <div className="flex items-center gap-2">
 *     <span className="px-2 py-1 bg-blue-100 rounded">New</span>
 *     <CellText>{value}</CellText>
 *   </div>
 * )
 * ```
 *
 * ## When NOT to Use
 *
 * ❌ Don't use for simple text cells:
 * ```tsx
 * { id: 'name', type: 'text' }  // ✅ Use built-in type instead
 * ```
 *
 * ❌ Don't use for status pills:
 * ```tsx
 * renderCell: (value) => <PillCell value={value} variant="active" />  // ✅ Use PillCell
 * ```
 *
 * ❌ Don't use when returning plain primitives:
 * ```tsx
 * renderCell: (value) => value  // ✅ TableCell auto-wraps primitives
 * ```
 *
 * ## Variants
 *
 * - `primary` (default): Standard table text (14px, weight 400, black)
 * - `secondary`: Lighter text for supporting info (14px, weight 400, slate-600)
 *
 * @see TableCell - Implements auto-wrapping for primitives
 * @see PillCell - For status pills and badges
 */

import React from 'react';
import { defaultTheme, getTextStyles } from '../theme/tokens';

interface CellTextProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
}

export const CellText: React.FC<CellTextProps> = ({
  children,
  variant = 'primary',
  className,
}) => {
  const textStyles = getTextStyles(defaultTheme);
  const style = variant === 'primary' ? textStyles.primary() : textStyles.secondary();

  return (
    <span style={style} className={className} data-cell-text={variant}>
      {children}
    </span>
  );
};
